import { CurrencyPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, QueryParamsHandling, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { DemandRequest } from '@app/core/models/demand/demand-request';
import { NdfCalculatorFilterRequest } from '@app/core/models/ndf-calculator-filter/ndf-calculator-filter-request';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { SortEvent, TableAction } from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { setSelectedDemand } from '@app/state/demand/demand.actions';
import { IdValue } from '@interfaces/a-organizar/idValue';
import { NdfDemandsCalculationsFilteredSummary } from '@interfaces/a-organizar/ndfDemandsCalculationsFilteredSummary';
import { NdfDemandsCalculationsFilteredSummaryParams } from '@interfaces/a-organizar/ndfDemandsCalculationsFilteredSummaryParams';
import { NdfOperationDates } from '@interfaces/a-organizar/ndfOperationDates';
import { TabViewItem } from '@interfaces/a-organizar/tabViewItem';
import { Company } from '@interfaces/common/company';
import { InternationalCurrency } from '@interfaces/common/internationalCurrencies';
import { Option } from '@interfaces/common/option';
import { Pagination, TablePagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { ConfirmDialogService } from '@services/helpers/dialog.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { ComexHttpService } from '@services/http/comex/comex-operation-http.service';
import { CompanyService } from '@services/http/company.service';
import { DerivativeReportHttpService } from '@services/http/derivative-report/derivative-report-http.service';
import {
  DerivativeMtmHttpService,
  DerivativeOperationDemandFilterDto,
} from '@services/http/derivatives/mtm/derivatives-mtm.service';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import { LoanOperationHttpService } from '@services/http/loanOperation/loan-operation-http.service';
import { NdfCalculatorService } from '@services/http/ndf-calculator.service';
import { openFileInNewTab } from '@src/app/core/utils/download-file.utils';
import { nameof } from '@src/app/core/utils/nameof.utils';
import DerivativeMtmResultsModalComponent from '@src/app/features/dashboard/control/derivative-management/mtm/modal/derivative-mtm-results.modal';
import { CardSummaryAction } from '@src/app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-calculator-list/ndf-card-summary/ndf-card-summary.component';
import { NdfEditDemandComponent } from '@src/app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-edit-demand/ndf-edit-demand.component';
import { ActiveFilterConfig } from '@src/app/shared/interfaces/active-filter.interface';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { MenuItem } from 'primeng/api';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { Subject, Subscription } from 'rxjs';
import { finalize, first, takeUntil } from 'rxjs/operators';

@Component({
  templateUrl: './ndf-calculator-list.component.html',
  styleUrls: ['./ndf-calculator-list.component.scss'],
  standalone: false,
})
export class NdfCalculatorListComponent
  extends VaultBaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('paginator', { static: false })
  paginator!: Paginator;
  @ViewChild('resultsModal') resultsModal?: DerivativeMtmResultsModalComponent;

  debouncedDates$ = new Subject<NdfOperationDates>();
  subscription: Subscription | null = null;
  private destroy$ = new Subject<void>();
  activeFilter: boolean = false;
  selectedDemandId: string = '';
  isMobile: boolean = false;
  search: string = '';
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  ndfDemands: NdfDemandsCalculationsFilteredSummary[] = [];
  derivativeCalculatorPermissions = Permissions.DerivativeCalculator;
  companyOptions: Option[] = [];
  selectedCompany: Option[] | null = null;
  companyDialogOptions: Option[] = [];
  selectedCompanyDialog: Option | null = null;
  operationOptions: Option[] = [
    { label: this.t(this.tk.Management.NdfImport), value: 'Import' },
    { label: this.t(this.tk.Management.NdfExport), value: 'Export' },
    { label: this.t(this.tk.Management.NdfLoan), value: 'Loan4131' },
    { label: this.t(this.tk.Management.Indicative), value: 'Indicative' },
  ];
  selectedOperation: Option | null = null;
  currencyOptions: Option[] = [];
  selectedCurrency: Option | null = null;
  tabViewItems: TabViewItem[] = [];
  tabViewActiveIndex = 0;
  ndfDemandFilter: NdfDemandsCalculationsFilteredSummaryParams = {};
  currentDate: Date = new Date();
  minDueDate: Date | null = null;
  errorMessage: string = '';

  public demandRegisterProps = nameof<DemandRequest>();

  financialInstitutionsOptions: Option[] = [];
  selectedFinancialInstitution: Option | null = null;
  maxTransactionValue: number = 10000;
  firstRow: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50, 100, 500, 1000];
  paginationRequest: TablePagination = {
    Page: 0,
    PageSize: 10,
    SortBy: '',
    SortDirection: '',
  };
  pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 10,
  };

  demandForm = this.initializeDemandForm();
  @ViewChild('editDemand')
  ndfEditDemandComponent!: NdfEditDemandComponent;
  comexOperations: Option[] = [];
  loanOperations: Option[] = [];
  selectedComexOperation: Option | null = null;
  selectedLoanOperation: Option | null = null;

  // Propriedades para alternar entre visualizações
  viewMode: 'cards' | 'table' = 'table';

  // Propriedades para o novo padrão de filtros
  currentNdfFilters: NdfCalculatorFilterRequest | null = null;
  activeFilterConfigs: ActiveFilterConfig[] = [];

  // Definições de colunas para o app-table
  columnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({
      field: 'sequentialId',
      header: 'ID',
      group: 'Identificação',
      sortable: true,
      sortField: 'sequentialId',
      style: { 'min-width': '80px' },
    }),
    new ColumnDefinition({
      field: 'company',
      header: this.t(this.tk.Management.Company),
      group: 'Identificação',
      sortable: true,
      sortField: 'company',
      style: { 'min-width': '150px' },
    }),
    new ColumnDefinition({
      field: 'reference',
      header: this.t(this.tk.Management.NdfReference),
      group: 'Identificação',
      sortable: true,
      sortField: 'reference',
      style: { 'min-width': '150px' },
    }),

    new ColumnDefinition({
      field: 'relatedOperations',
      header: 'Operações Relacionadas',
      group: 'Identificação',
      sortable: false,
      style: { 'min-width': '150px' },
    }),
    new ColumnDefinition({
      field: 'operation',
      header: this.t('Tipo de operação'),
      group: 'Operação',
      sortable: true,
      sortField: 'operation',
      style: { 'min-width': '120px' },
    }),

    new ColumnDefinition({
      field: 'totalOperations',
      header: this.t(this.tk.Management.NdfOperations),
      group: 'Operação',
      sortable: true,
      sortField: 'totalOperations',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      field: 'currency',
      header: this.t(this.tk.Management.NdfCurrency),
      group: 'Operação',
      sortable: true,
      sortField: 'currency',
      style: { 'min-width': '100px' },
    }),
    new ColumnDefinition({
      field: 'transactionValue',
      header: this.t(this.tk.Management.NdfValue),
      group: 'Financeiro',
      sortable: true,
      sortField: 'transactionValue',
      format: 'currency:2',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      field: 'budgetRate',
      header: this.t(this.tk.Management.BudgetRate1),
      group: 'Financeiro',
      sortable: true,
      sortField: 'budgetRate',
      format: 'currency:4',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      field: 'dueDate',
      header: this.t('Vencimento'),
      group: 'Datas',
      sortable: true,
      sortField: 'dueDate',
      format: 'date',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      field: 'createdAt',
      header: this.t('Criado em'),
      group: 'Datas',
      sortable: true,
      sortField: 'createdAt',
      format: 'date',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      selected: false,
      field: 'userName',
      header: this.t('Criado por'),
      group: 'Auditoria',
      sortable: true,
      sortField: 'userName',
      style: { 'min-width': '120px' },
    }),
  ];

  // Cache de menus por ID de item
  private menuActionsCache = new Map<string, MenuItem[]>();

  private initializeDemandForm() {
    return this.formBuilder.group({
      [this.demandRegisterProps.companyId]: ['', [Validators.required]],
      [this.demandRegisterProps.operationType]: ['', [Validators.required]],
      [this.demandRegisterProps.internationalCurrencyId]: [''],
      [this.demandRegisterProps.transactionValue]: ['0'],
      [this.demandRegisterProps.budgetRate]: ['0'],
      [this.demandRegisterProps.dueAt]: [''],
      [this.demandRegisterProps.reference]: [''],
      comexOperationId: [''],
      loanOperationId: [''],
    });
  }

  constructor(
    translateService: TranslateService,
    private toast: ToastHelperService,
    private formErrorHandlerService: FormErrorHandlerService,
    private ndfCalculatorService: NdfCalculatorService,
    private dateFormatter: DateFormatterService,
    private currencyPipe: CurrencyPipe,
    private companyService: CompanyService,
    private dialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private financialInstitutionService: FinancialInstitutionService,
    private cdr: ChangeDetectorRef,
    private derivativeReportHttpService: DerivativeReportHttpService,
    private comexHttpService: ComexHttpService,
    private loanOperationService: LoanOperationHttpService,
    private derivativeMtmHttp: DerivativeMtmHttpService
  ) {
    super(translateService, store);
  }

  async ngOnInit() {
    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.Management),
        routerLink: '/dashboard',
      },
      {
        label: this.t(this.tk.Management.Derivatives),
      },
      {
        label: this.t(this.tk.Management.NdfDemandManagement),
        routerLink: '/dashboard/ndf-calculator/list',
      },
    ];

    // Carregar filtros da URL inicialmente
    this.loadFiltersFromUrl();

    // Escutar mudanças na URL para sincronizar filtros
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const previousFilters = JSON.stringify(this.currentNdfFilters);
      this.loadFiltersFromUrl();
      const newFilters = JSON.stringify(this.currentNdfFilters);

      // Se os filtros mudaram, recarregar dados
      if (previousFilters !== newFilters) {
        this.getNdfDemands();
      }
    });

    // Inicializar o filtro de busca da URL
    this.search = this.route.snapshot.queryParams['filter'] || '';
    this.ndfDemandFilter = {
      ...this.ndfDemandFilter,
      filter: this.search,
    };

    this.fillTabViewItems();
    this.getCompanysOptions();
    this.getCurrencyOptions();
    this.getFinancialInstitutions();
    this.getDemandsMaxValue();
    // this.getNdfDemands(); // REMOVIDO: busca inicial só após carregar empresas
  }

  ngAfterViewInit(): void {
    this.verifyQueryParams();
  }

  verifyQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      if (params['triggerAddNdfQuotation']) {
        this.addNdfQuotation();
      }
    });
  }

  getDemandsMaxValue(): void {
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.getDemandsNdfMaxValue().subscribe({
      next: response => {
        this.maxTransactionValue = response as number;
        this.utilsService.setIsLoading(false);
      },
      error: error => {
        console.error('error: ', error);
        this.utilsService.setIsLoading(false);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingDemandMaxValue),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  handleSuccessUpdate() {
    this.getNdfDemands();
    this.ndfEditDemandComponent.close();
  }

  getOperationCurrency(currencyId: string): string {
    if (!currencyId) return '';
    return this.currencyOptions.find(currency => currency.value === currencyId)?.label || '';
  }

  getCurrencyOptions(): void {
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.getInternationalCurrencies().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.currencyOptions = (response || []).map(
            (currency: InternationalCurrency) =>
              ({
                label: currency.currencyCode,
                value: currency.id,
              }) as Option
          );
        }
      },
      error: error => {
        console.error('error: ', error);
        this.utilsService.setIsLoading(false);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingInternacionalCurrencies),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  handleInputNumber(inputData: IdValue, key: string): void {
    const value = inputData?.value || 0;
    switch (key) {
      case 'transactionValue':
        this.demandForm.patchValue({
          [this.demandRegisterProps.transactionValue]: value.toString(),
        });
        break;
      case 'budgetRate':
        this.demandForm.patchValue({
          [this.demandRegisterProps.budgetRate]: value.toString(),
        });
        break;
      default:
        break;
    }
  }

  handleCalendarValue(dates: string[], key: string): void {
    switch (key) {
      case 'dueAt':
        this.demandForm.patchValue({
          [this.demandRegisterProps.dueAt]: dates.join(','),
        });
        break;
    }
  }

  setOperationDates(dates: NdfOperationDates): void {
    this.utilsService.setIsLoading(true);
    const ndfDates = {
      ...dates,
      operationDate: dates.operationDate.split('T')[0],
      dueAt: dates?.dueAt?.split('T')[0] || null,
    } as NdfOperationDates;

    if (!ndfDates.operationDate.length) {
      ndfDates.operationDate = new Date().toISOString().slice(0, 10);
    }

    this.debouncedDates$.next(ndfDates);
  }

  getFinancialInstitutions() {
    this.utilsService.setIsLoading(true);
    this.financialInstitutionService.getFinancialInstitutions({ PageSize: 1000 }).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.financialInstitutionsOptions = (response?.data || []).map(item => {
            return {
              label: `${item.displayName} | ${item.compensationCode}`,
              value: item.id,
              data: item,
            };
          });
        }
      },
      error: error => {
        console.error(error);
        this.utilsService.setIsLoading(false);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingFinancialInstitutions),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  fillTabViewItems(): void {
    this.tabViewItems = [
      {
        title: this.t(this.tk.User.All),
        count: 0,
        tabindex: 0,
        content: '',
      },
      {
        title: this.t(this.tk.Management.NdfSimulations),
        count: 0,
        tabindex: 0,
        content: '',
      },
      {
        title: this.t(this.tk.Management.NdfListed),
        count: 0,
        tabindex: 0,
        content: '',
      },
      {
        title: this.t(this.tk.Management.NdfContractors),
        count: 0,
        tabindex: 0,
        content: '',
      },
    ];
  }

  handleTabView(tabIndex: number): void {
    this.tabViewActiveIndex = tabIndex;
  }

  getCompanysOptions(): void {
    this.utilsService.setIsLoading(true);
    this.companyService.getAllCompanys().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        const options = (response?.data || []).map(
          (company: Company) =>
            ({
              label: company.displayName,
              value: company.id,
            }) as Option
        );
        this.companyOptions = options;
        this.companyDialogOptions = options;

        // --- INÍCIO: Seleção automática se houver companyId na query string ---
        const queryParams = this.route.snapshot.queryParams;
        const companyId = queryParams['companyId'];
        if (companyId) {
          // companyId pode ser string ou array, mas normalmente é string
          const selected = this.companyOptions.filter(opt =>
            Array.isArray(companyId) ? companyId.includes(opt.value) : opt.value == companyId
          );
          this.selectedCompany = selected;
          if (selected.length > 0) {
            this.ndfDemandFilter = {
              ...this.ndfDemandFilter,
              companysIds: selected.map(opt => opt.value),
            };
            // Só atualiza a URL se já houver companyId (ou seja, seleção manual)
            // this.updateUrlWithCompanyId(selected[0].value); // REMOVIDO
            this.getNdfDemands();
          }
        } else if (this.companyOptions.length > 0) {
          // Se não houver companyId, NÃO selecionar nenhuma empresa
          this.selectedCompany = [];
          this.ndfDemandFilter = {
            ...this.ndfDemandFilter,
            companysIds: [],
          };
          this.getNdfDemands();
        } else {
          // Se não houver empresas, busca padrão
          this.getNdfDemands();
        }
        // --- FIM: Seleção automática ---
      },
      error: error => {
        console.error('error: ', error);
        this.utilsService.setIsLoading(false);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingCompanys),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  // Atualiza a URL com o companyId
  private updateUrlWithCompanyId(companyId: string) {
    const queryParams = { ...this.route.snapshot.queryParams, companyId };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  getNdfDemands(): void {
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService
      .getNdfDemandsCalculationsFilteredSummary({
        ...this.ndfDemandFilter,
        pageSize: this.paginationRequest?.PageSize || 10,
        page: (this.paginationRequest?.Page || 0) + 1,
        sortColumn: this.paginationRequest?.SortBy || '',
        sortDirection: this.paginationRequest?.SortDirection || '',
      })
      .subscribe({
        next: response => {
          this.utilsService.setIsLoading(false);
          if (response) {
            this.ndfDemands = response.data || [];
            const { total, currentPage, pages, itensPerPage } = response as unknown as Pagination;
            this.pagination = {
              total,
              currentPage,
              pages,
              itemsPerPage: itensPerPage as number,
            };
            // Limpar cache de menus quando os dados mudarem
            this.menuActionsCache.clear();
          }
        },
        error: error => {
          this.utilsService.setIsLoading(false);
          console.log('getNdfDemands error: ', error);

          this.toast.showToastError(
            this.t(this.tk.Management.ErrorLoadingDemands),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  private clearSelections(): void {
    this.selectedCompanyDialog = null;
    this.selectedOperation = null;
    this.selectedCurrency = null;
    this.selectedFinancialInstitution = null;
  }

  addNdfQuotation(): void {
    this.clearSelections();
    this.demandForm.reset();
    this.cdr.detectChanges();

    this.dialogService.confirm(
      '',
      this.t(this.tk.Management.NdfCreateDemand),
      '',
      '',
      '',
      'create-demand',
      false,
      false
    );
  }

  loadComexOperations(): void {
    const companyId = this.demandForm.get(this.demandRegisterProps.companyId)?.value;
    if (!companyId) return;

    this.utilsService.setIsLoading(true);
    this.comexHttpService.getAllFiltered({ page: 1, pageSize: 1000, companyId }).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response?.data) {
          this.comexOperations = response.data.map(op => ({
            label: op.comexNegotiation.reference,
            value: op.id,
            data: op,
          }));
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingOperations),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  loadLoanOperations(): void {
    const companyId = this.demandForm.get(this.demandRegisterProps.companyId)?.value;
    if (!companyId) return;

    this.utilsService.setIsLoading(true);
    this.loanOperationService.filter({ page: 1, pageSize: 100 }, companyId).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response?.data) {
          this.loanOperations = response.data
            .filter(op => op.id && op.reference)
            .map(op => ({
              label: op.reference as string,
              value: op.id as string,
              data: op,
            })) as Option[];
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingOperations),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  saveNdfQuotation(): void {
    if (this.demandForm.invalid) {
      this.formErrorHandlerService.markAllAsTouched(this.demandForm);
      return;
    }

    const {
      companyId,
      dueAt,
      internationalCurrencyId,
      operationType,
      reference,
      transactionValue,
      budgetRate,
      comexOperationId,
      loanOperationId,
    } = this.demandForm.getRawValue();

    const demand = {
      id: null,
      companyId: companyId || null,
      operationType: operationType || '',
      internationalCurrencyId: internationalCurrencyId || '',
      transactionValue: transactionValue ? parseInt(transactionValue) : 0,
      dueAt: dueAt ? dueAt.split('T')[0] : null,
      reference: reference || '',
      annualCostByScenarios: [],
      operations: [],
      budgetRate: parseFloat(budgetRate as string) || 0,
      comexOperationId: comexOperationId || undefined,
      loanOperationId: loanOperationId || undefined,
    };

    let demandId = null;
    const comexOperation = this.comexOperations.find(op => op.value === comexOperationId);
    const loanOperation = this.loanOperations.find(op => op.value === loanOperationId);

    demandId =
      comexOperation?.data?.derivativeOperationDemand?.id ||
      loanOperation?.data?.derivativeOperationDemand?.id ||
      null;

    if (demandId) {
      this.router.navigate([
        `/dashboard/control/derivative-management/derivatives/list?demandId=${demandId}`,
      ]);
      return;
    }

    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.postDerivativesDemandSimulation(demand).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);

        if (response) {
          if (this.store) {
            this.store.dispatch(
              setSelectedDemand({
                demand: response,
              })
            );
          }
          localStorage.setItem('selectedDemand', JSON.stringify(response));
          this.router.navigate(['/dashboard/control/derivative-management/derivatives/list'], {
            queryParams: { demandId: response.id },
          });
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('error: ', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorCreatingDemandSimulation),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  handleCardSummaryAction(actionSummary: CardSummaryAction) {
    const { action, item } = actionSummary;
    switch (action) {
      case 'remove':
        this.handleDeleteDemand(item?.id as string);
        break;
      case 'duplicate':
        this.handleDuplicateDemand(item?.id as string);
        break;
      case 'export':
        this.handleExportDemand(item?.id as string);
        break;
      case 'edit':
        this.ndfEditDemandComponent.open(actionSummary.item?.id as string);
        break;

      default:
        break;
    }
  }

  handleExportDemand(demandId: string): void {
    this.utilsService.setIsLoading(true);
    this.derivativeReportHttpService
      .getOperationsSummaryReportByDemandId(demandId)
      .pipe(
        first(),
        finalize(() => this.utilsService.setIsLoading(false))
      )
      .subscribe({
        next: response => {
          if (response) {
            openFileInNewTab(response);
          }
          this.toast.showToastSuccess(this.t(this.tk.General.OperationsExportedSuccessfully));
        },
        error: () =>
          this.toast.showToastError(
            this.t(this.tk.Derivatives.ReportErrorTitle),
            this.t(this.tk.Derivatives.ReportErrorDescription)
          ),
        complete: () => {},
      });
  }

  handleDuplicateDemand(id: string): void {
    if (!id) return;
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.getDemandById(id).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          let demand = response;
          const operations = demand.operations.map(operation => {
            return {
              ...operation,
              id: null,
              sequentialId: undefined,
              operationStatus: 'Simulated',
              composition: undefined,
              financialStatus: undefined,
            };
          });
          demand = {
            ...demand,
            id: null,
            operations,
          };
          if (this.store) {
            this.store.dispatch(
              setSelectedDemand({
                demand,
              })
            );
          }
          localStorage.setItem('selectedDemand', JSON.stringify(demand));
          this.router.navigate(['/dashboard/control/derivative-management/derivatives/list'], {
            queryParams: { demandId: demand.id },
          });
        }
      },
      error: error => {
        console.error('error: ', error);
        this.utilsService.setIsLoading(false);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingDemand),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  handleDeleteDemand(id: string): void {
    if (!id) return;
    this.dialogService
      .confirm(
        '',
        this.t(this.tk.Management.NdfDemandTitleDeleteConfirmation),
        this.t(this.tk.General.Delete),
        this.t(this.tk.General.Cancel),
        '',
        'demand-should-remove'
      )
      .then(response => {
        this.utilsService.setIsLoading(true);
        if (response) {
          this.ndfCalculatorService.deleteDerivativesDemand(id).subscribe({
            next: () => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastSuccess(
                this.t(this.tk.Management.NdfDemandDeletedSuccessfully),
                ''
              );
              this.getNdfDemands();
            },
            error: error => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastError(
                this.t(this.tk.Management.ErrorRemovingDemand),
                error?.errors?.length
                  ? error.errors[0].message
                  : this.t(this.tk.General.RequestError)
              );
              console.error('error: ', error);
              this.errorMessage =
                error?.errors[0]?.message || (this.t(this.tk.General.RequestError) as string);
            },
          });
        }
      })
      .catch(error => {
        console.error('Error: ', error);
      });
  }

  handleOptionsChange(selectedOptions: Option[] | null, key: string): void {
    switch (key) {
      case 'selectedCompany':
        this.ndfDemandFilter = {
          ...this.ndfDemandFilter,
          companysIds: (selectedOptions as Option[])?.map(x => x.value),
        };
        if (selectedOptions && selectedOptions.length > 0) {
          // Só atualiza a URL quando o usuário seleciona manualmente
          this.updateUrlWithCompanyId(selectedOptions[0].value);
        }
        this.getNdfDemands();
        break;
      default:
        break;
    }
  }
  handleOptionChange(selectedOptions: Option | null, key: string): void {
    // Declare todas as variáveis no início da função
    let previousCompanyId: string | undefined;
    let newCompanyId: string | undefined;
    let operationType: string | undefined;

    switch (key) {
      case 'companyId':
        // Salvar a empresa selecionada anteriormente para verificar se mudou
        previousCompanyId = this.selectedCompanyDialog?.value;
        newCompanyId = (selectedOptions as Option)?.value;

        // Atualizar a seleção de empresa
        this.selectedCompanyDialog = selectedOptions as Option;

        // Resetar o formulário completamente se a empresa mudou
        if (previousCompanyId !== newCompanyId) {
          // Limpar todas as seleções
          this.selectedComexOperation = null;
          this.selectedLoanOperation = null;
          this.selectedCurrency = null;
          this.comexOperations = [];
          this.loanOperations = [];

          // Reinicializar o formulário inteiro
          this.demandForm = this.initializeDemandForm();

          // Definir apenas o ID da empresa
          this.demandForm.patchValue({
            [this.demandRegisterProps.companyId]: newCompanyId,
          });

          // Remover todos os validadores específicos
          this.removeIndicativeValidators();
          this.demandForm.get('comexOperationId')?.clearValidators();
          this.demandForm.get('loanOperationId')?.clearValidators();
          this.demandForm.updateValueAndValidity();

          // Forçar detecção de mudanças para atualizar a interface
          this.cdr.detectChanges();

          // Se havia um tipo de operação selecionado, chamar handleOptionChange novamente
          // para atualizar as operações para a nova empresa
          if (this.selectedOperation) {
            // Guardar operação atual
            const currentOperation = this.selectedOperation;
            // Resetar a operação
            this.selectedOperation = null;
            // Chamar novamente com a operação anterior
            setTimeout(() => {
              this.handleOptionChange(currentOperation, 'operationType');
            }, 0);
          }
        } else {
          // Se a empresa não mudou, apenas atualize o valor
          this.demandForm.patchValue({
            [this.demandRegisterProps.companyId]: newCompanyId,
          });
        }
        break;

      case 'operationType':
        if (!this.selectedCompanyDialog?.value) {
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorCreatingDemandSimulation),
            this.t(this.tk.Management.Company)
          );
          return;
        }

        operationType = (selectedOptions as Option)?.value;
        this.selectedOperation = selectedOptions as Option;
        this.demandForm.patchValue({
          [this.demandRegisterProps.operationType]: operationType,
        });

        // Limpar todas as seleções anteriores
        this.selectedComexOperation = null;
        this.selectedLoanOperation = null;
        this.demandForm.patchValue({
          comexOperationId: null,
          loanOperationId: null,
        });

        // Reset form fields and load appropriate operations based on operation type
        if (operationType === 'Indicative') {
          this.comexOperations = [];
          this.loanOperations = [];

          // Add validators for indicative fields
          this.demandForm
            .get(this.demandRegisterProps.internationalCurrencyId)
            ?.setValidators([Validators.required]);
          this.demandForm
            .get(this.demandRegisterProps.transactionValue)
            ?.setValidators([Validators.required, Validators.min(0)]);
          this.demandForm
            .get(this.demandRegisterProps.budgetRate)
            ?.setValidators([Validators.required, Validators.min(0)]);
          this.demandForm.get(this.demandRegisterProps.dueAt)?.setValidators([Validators.required]);
          this.demandForm
            .get(this.demandRegisterProps.reference)
            ?.setValidators([Validators.required]);

          // Remover validadores dos outros campos
          this.demandForm.get('comexOperationId')?.clearValidators();
          this.demandForm.get('loanOperationId')?.clearValidators();
        } else if (['Import', 'Export'].includes(operationType || '')) {
          // Limpar operações de empréstimo
          this.loanOperations = [];

          // Carregar operações comex para a empresa atual
          this.loadComexOperations();

          // Adicionar validadores
          this.demandForm.get('comexOperationId')?.setValidators([Validators.required]);
          this.demandForm.get('loanOperationId')?.clearValidators();

          // Remove validators from indicative fields
          this.removeIndicativeValidators();
        } else if (operationType === 'Loan4131') {
          // Limpar operações comex
          this.comexOperations = [];

          // Carregar operações de empréstimo para a empresa atual
          this.loadLoanOperations();

          // Adicionar validadores
          this.demandForm.get('loanOperationId')?.setValidators([Validators.required]);
          this.demandForm.get('comexOperationId')?.clearValidators();

          // Remove validators from indicative fields
          this.removeIndicativeValidators();
        }

        // Update form validation
        this.demandForm.updateValueAndValidity();

        // Forçar detecção de mudanças para atualizar a interface
        this.cdr.detectChanges();
        break;

      case 'internationalCurrencyId':
        this.selectedCurrency = selectedOptions as Option;
        this.demandForm.patchValue({
          [this.demandRegisterProps.internationalCurrencyId]: (selectedOptions as Option)?.value,
        });
        break;

      case 'comexOperation':
        this.selectedComexOperation = selectedOptions as Option;
        this.demandForm.patchValue({
          comexOperationId: (selectedOptions as Option)?.value,
        });
        break;

      case 'loanOperation':
        this.selectedLoanOperation = selectedOptions as Option;
        this.demandForm.patchValue({
          loanOperationId: (selectedOptions as Option)?.value,
        });
        break;

      default:
        break;
    }
  }

  private removeIndicativeValidators(): void {
    this.demandForm.get(this.demandRegisterProps.internationalCurrencyId)?.clearValidators();
    this.demandForm.get(this.demandRegisterProps.transactionValue)?.clearValidators();
    this.demandForm.get(this.demandRegisterProps.budgetRate)?.clearValidators();
    this.demandForm.get(this.demandRegisterProps.dueAt)?.clearValidators();
    this.demandForm.get(this.demandRegisterProps.reference)?.clearValidators();
  }

  resetFilter(): void {
    // Preservar o pageSize atual
    const currentPageSize = this.paginationRequest.PageSize || 10;

    this.ndfDemandFilter = {};
    this.selectedCompany = [];
    this.currentNdfFilters = null;
    this.activeFilterConfigs = [];
    this.handleFilterActive(false);

    // Resetar paginação mantendo o pageSize
    this.paginationRequest = {
      Page: 0,
      PageSize: currentPageSize,
      SortBy: '',
      SortDirection: '',
    };

    // Limpar URL dos filtros
    this.updateUrlWithFilters();

    this.getNdfDemands();
  }

  handleSearch(search: string = ''): void {
    this.search = search;
    this.ndfDemandFilter = {
      ...this.ndfDemandFilter,
      filter: search,
    };
    this.resetPagination();
    this.updateUrlWithFilters();
    this.getNdfDemands();
  }

  /**
   * Resets the "page" property of "paginationRequest", ndfDemandFilter and p-paginator to default value
   */
  resetPagination(): void {
    this.ndfDemandFilter = {
      ...this.ndfDemandFilter,
      page: 0,
    };
    this.paginationRequest = {
      ...this.paginationRequest,
      Page: 0,
    };
    this.paginator.changePage(0);
  }

  handleFilter(filter: NdfCalculatorFilterRequest) {
    // Salvar os filtros atuais para o novo padrão
    this.currentNdfFilters = filter;

    const {
      internationalCurrencyId,
      companyId,
      dateFrom,
      dateTo,
      notionalAmountFrom,
      notionalAmountTo,
      operationType,
      reference,
      withHiredOperations,
      withHiredOperationsOpen,
    } = filter;

    // Preservar o pageSize atual
    const currentPageSize = this.paginationRequest.PageSize || 10;

    this.ndfDemandFilter = {
      ...this.ndfDemandFilter,
      internationalCurrencyId,
      companyId,
      dateDueAtFrom: dateFrom,
      dateDueAtTo: dateTo,
      notionalAmountFrom,
      notionalAmountTo,
      operationType,
      reference,
      withHiredOperations,
      withHiredOperationsOpen,
    };

    this.handleFilterActive(false);

    // Resetar paginação mantendo o pageSize
    this.paginationRequest = {
      ...this.paginationRequest,
      Page: 0,
      PageSize: currentPageSize,
      SortBy: this.paginationRequest.SortBy || '',
      SortDirection: this.paginationRequest.SortDirection || '',
    };

    // Atualizar URL com filtros
    this.updateUrlWithFilters();

    this.getNdfDemands();
  }

  handleFilterActive(activeFilter: boolean): void {
    this.activeFilter = activeFilter;
  }

  handleDialogAction(dialogAction: string): void {
    if (!dialogAction) return;

    switch (dialogAction) {
      case 'back':
        this.addNdfQuotation();
        break;
    }
  }

  get selectedCompanyLabel(): string {
    return this.selectedCompanyDialog?.label || '';
  }

  get selectedCurrencyLabel(): string {
    return this.selectedCurrency?.label || '';
  }

  get modalValue(): string {
    return this.demandForm.get(this.demandRegisterProps.transactionValue)?.value || '';
  }

  get modalDueAt(): string {
    return this.demandForm.get(this.demandRegisterProps.dueAt)?.value || '';
  }

  get modalRemarks(): string {
    return this.demandForm.get(this.demandRegisterProps.reference)?.value || '';
  }

  get deadlineBusinessDaysLabel(): string {
    return `${this.t(this.tk.Management.NdfDeadline)} (${this.t(this.tk.Management.NdfWorkingDays)})`;
  }

  get deadlineDaysLabel(): string {
    return `${this.t(this.tk.Management.NdfDeadline)} (${this.t(this.tk.Management.NdfCalendarDays)})`;
  }

  get activeFilters(): Option[] {
    const filters: Option[] = [];
    const filterKeys = Object.keys(this.ndfDemandFilter);

    const operationTypes = {
      Loan4131: this.t(this.tk.Management.NdfLoan),
      Import: this.t(this.tk.Management.NdfImport),
      Export: this.t(this.tk.Management.NdfExport),
      Indicative: this.t(this.tk.Management.Indicative),
    };

    filterKeys.forEach((key: string) => {
      const filter = this.ndfDemandFilter[key as keyof NdfDemandsCalculationsFilteredSummaryParams];

      let label = filter as string;
      let group = '';

      switch (key) {
        case 'internationalCurrencyId':
          label = this.currencyOptions.find(currency => currency.value === filter)?.label || key;
          group = this.t(this.tk.Management.NdfCurrency);
          break;
        case 'companyId':
          label = this.companyOptions.find(company => company.value === filter)?.label || key;
          group = this.t(this.tk.Management.user);
          break;
        case 'companysIds': {
          const companyLabels = filter as string[];

          if (!companyLabels || !companyLabels.length) return;

          const labels = companyLabels
            ?.map(id => this.companyOptions.find(company => company.value === id)?.label)
            .filter(label => label) // Remove undefined values
            .join(', ');

          label = labels || key;
          group = this.t(this.tk.Management.user);

          break;
        }
        case 'dateDueAtFrom':
          this.dateFormatter.formatDate(filter as string).subscribe({
            next: response => {
              label = response;
            },
          });
          group = filters.find(f => f.key === 'dateTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfDueDate);
          break;
        case 'dateDueAtTo':
          this.dateFormatter.formatDate(filter as string).subscribe({
            next: response => {
              label = response;
            },
          });
          group = filters.find(f => f.key === 'dateFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfDueDate);
          break;
        case 'operationType':
          label = operationTypes[filter as keyof typeof operationTypes];
          group = this.t(this.tk.Management.DemandType);
          break;
        case 'notionalAmountFrom':
          label = this.currencyPipe.transform(filter as string, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'notionalAmountTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfValue);
          break;
        case 'notionalAmountTo':
          label = this.currencyPipe.transform(filter as string, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'notionalAmountFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfValue);
          break;
        case 'operationId':
          label = this.operationOptions.find(operation => operation.value === filter)?.label || key;
          group = this.t(this.tk.Management.NdfOperation);
          break;
        case 'reference':
          label = filter as string;
          group = this.t(this.tk.Management.NdfReference);
          break;
        case 'withHiredOperationsOpen':
          label = this.t(this.tk.Management.ContractsActiveAndOpen);
          group = '';
          break;
        case 'withHiredOperations':
          label = this.t(this.tk.Management.ContractedOperations);
          group = '';
          break;
        default:
          label = filter as string;
          group = '';
          break;
      }

      if (filter) {
        filters.push({
          label,
          key,
          value: filter as string,
          group,
        });
      }
    });

    return filters;
  }

  handleFilterPill(filter: Option): void {
    this.ndfDemandFilter = {
      ...this.ndfDemandFilter,
      [filter.key as keyof NdfDemandsCalculationsFilteredSummaryParams]: '',
    };

    this.clearOptionFilter(filter.key as string);
    this.getNdfDemands();
  }

  clearOptionFilter(filter: string) {
    switch (filter) {
      case 'internationalCurrencyId':
        this.selectedCurrency = null;
        break;
      case 'companyId':
        this.selectedCompany = null;
        break;
      case 'companysIds':
        this.selectedCompany = [];
        break;
      case 'operationId':
        this.selectedOperation = null;
        break;
      case 'filter':
        this.handleSearch('');
        break;
    }
  }

  fillOperation(isCollapsed: boolean, index: number): void {
    if (isCollapsed) {
      const selectedDemand = this.ndfDemands[index];

      if (selectedDemand) {
        this.utilsService.setIsLoading(true);
        this.ndfCalculatorService.getDemandById(selectedDemand.id as string).subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            if (response) {
              this.ndfDemands[index].operations = response?.operations.map(operation => {
                return {
                  ...operation,
                  internationalCurrency: this.ndfDemands[index].currency,
                  operationType: this.ndfDemands[index].operation,
                  transactionValue: this.ndfDemands[index].transactionValue || 0,
                };
              });
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error('error: ', error);

            this.toast.showToastError(
              this.t(this.tk.Management.ErrorLoadingDemand),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      }
    }
  }

  handlePagination(pagination: PaginatorState) {
    this.paginationRequest = {
      Page: pagination.page,
      PageSize: pagination.rows,
    };
    this.getNdfDemands();
  }

  handleTableSort({ field, direction }: SortEvent): void {
    const columnDef = this.columnDefinitions.find(col => col.header === field);
    const sortField = columnDef?.sortField || field;

    // Atualizar os parâmetros de paginação com ordenação
    this.paginationRequest = {
      ...this.paginationRequest,
      SortBy: sortField || '',
      SortDirection: direction || '',
    };

    console.log('handleTableSort', this.paginationRequest);
    this.getNdfDemands();
  }

  get showComexOperations(): boolean {
    return ['Import', 'Export'].includes(this.selectedOperation?.value || '');
  }

  get showLoanOperations(): boolean {
    return this.selectedOperation?.value === 'Loan4131';
  }

  get showIndicativeFields(): boolean {
    return this.selectedOperation?.value === 'Indicative';
  }

  // Métodos para alternância de visualização
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'cards' ? 'table' : 'table';
  }

  setViewMode(mode: 'cards' | 'table'): void {
    this.viewMode = mode;
  }

  // Método para retornar ações do menu da tabela
  getMenuActions(item: NdfDemandsCalculationsFilteredSummary): MenuItem[] {
    // Se não tem item ou ID, retorna array vazio
    if (!item?.id) {
      return [];
    }

    // Tentar obter do cache primeiro
    const cachedMenu = this.menuActionsCache.get(item.id);
    if (cachedMenu) {
      return cachedMenu;
    }

    // Se não está no cache, criar novo menu
    const actions: MenuItem[] = [];

    // Ação de editar (sempre visível se tem permissão)
    actions.push({
      label: this.t(this.tk.General.Edit),
      icon: 'pi pi-pencil',
      action: 'edit',
    });

    // Ação de duplicar
    actions.push({
      label: this.t(this.tk.General.Duplicate),
      icon: 'pi pi-clone',
      action: 'duplicate',
    });

    // Ação de remover
    actions.push({
      label: this.t(this.tk.General.Delete),
      icon: 'pi pi-trash',
      action: 'remove',
    });

    // Ação de exportar
    actions.push({
      label: this.t(this.tk.General.Export1),
      icon: 'pi pi-arrow-up',
      action: 'export',
    });

    // Calculadora de Derivativos
    actions.push({
      label: 'Calculadora de Derivativos',
      icon: 'pi pi-calculator',
      route: `/dashboard/control/derivative-management/derivatives/list?demandId=${item?.id}`,
    });

    // Operações de Derivativos
    actions.push({
      label: 'Operações de Derivativos',
      icon: 'pi pi-chart-bar',
      url: `/dashboard/ndf-operation/list?filter=${item?.sequentialId}`,
    });

    // MTM
    actions.push({
      label: 'MTM',
      icon: 'pi pi-chart-line',
      command: () => this.openMtmForDemand(item),
    });

    // Armazenar no cache
    this.menuActionsCache.set(item.id, actions);
    return actions;
  }

  openMtmForDemand(item: NdfDemandsCalculationsFilteredSummary): void {
    if (!item.id) return;

    const payload: DerivativeOperationDemandFilterDto = {
      page: 1,
      pageSize: 1000, // get all operations for the demand
      derivativeOperationDemandId: item.id,
    };

    this.utilsService.setIsLoading(true, 'Buscando dados de MTM...');
    this.derivativeMtmHttp
      .getFilteredWithMtm(payload)
      .pipe(
        first(),
        finalize(() => this.utilsService.setIsLoading(false))
      )
      .subscribe(result => {
        if (result && this.resultsModal) {
          this.resultsModal.open(result, '');
        } else {
          this.toast.showToastError('Erro', 'Não foi possível obter os dados de MTM.');
        }
      });
  }

  // Handler para ações da tabela (compatibilidade)
  handleTableAction({ action, item }: TableAction<NdfDemandsCalculationsFilteredSummary>): void {
    this.handleCardSummaryAction({ action, item });
  }

  // Método para paginação da tabela
  handleTablePagination(pagination: PaginatorState): void {
    this.handlePagination(pagination);
  }

  override ngOnDestroy(): void {
    if (this.store) {
      this.store.dispatch(setSelectedDemand({ demand: null }));
    }
    this.subscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Método helper para formatar referência (igual ao do card)
  getFormattedReference(ref: string): string {
    if (!ref) return '';
    if (ref.includes('WORKING_CAPITAL')) {
      const translated = this.t(this.tk.Costs.WorkingCapital);
      return ref.replace('WORKING_CAPITAL', translated);
    }
    return ref;
  }

  // Métodos para o novo padrão de filtros ativos
  onActiveFiltersChange(configs: ActiveFilterConfig[]): void {
    this.activeFilterConfigs = configs;
  }

  onRemoveFilter(propertyName: string): void {
    if (this.currentNdfFilters) {
      const newFilters = { ...this.currentNdfFilters };

      // Tratar casos especiais de range
      if (propertyName === 'notionalAmountFrom') {
        delete newFilters.notionalAmountFrom;
        delete newFilters.notionalAmountTo;
      } else if (propertyName === 'dateFrom') {
        delete newFilters.dateFrom;
        delete newFilters.dateTo;
      } else {
        delete newFilters[propertyName as keyof NdfCalculatorFilterRequest];
      }

      // Se não há mais filtros, limpar completamente
      this.currentNdfFilters = Object.keys(newFilters).length > 0 ? newFilters : null;

      // Atualizar URL com novos filtros
      this.updateUrlWithFilters();

      // Aplicar os novos filtros
      if (this.currentNdfFilters) {
        this.handleFilter(this.currentNdfFilters);
      } else {
        this.onClearAllFilters();
      }
    }
  }

  onClearAllFilters(): void {
    this.currentNdfFilters = null;
    this.activeFilterConfigs = [];
    this.updateUrlWithFilters();
    this.resetFilter();
  }

  // Método para traduzir o tipo de operação
  getOperationTypeLabel(operation: string): string {
    switch (operation) {
      case 'Import':
        return this.t(this.tk.Management.NdfImport);
      case 'Export':
        return this.t(this.tk.Management.NdfExport);
      case 'Loan4131':
        return this.t(this.tk.Management.NdfLoan);
      case 'Indicative':
        return this.t(this.tk.Management.Indicative);
      default:
        return operation;
    }
  }

  // Método para carregar filtros da URL
  loadFiltersFromUrl(): void {
    const queryParams = this.route.snapshot.queryParams;

    console.log('🔄 Loading filters from URL:', queryParams);

    // Resetar filtros primeiro
    this.currentNdfFilters = null;

    // Se não há filtros na URL, não fazer nada
    if (!queryParams || Object.keys(queryParams).filter(key => key !== 'filter').length === 0) {
      return;
    }

    const filters: Partial<NdfCalculatorFilterRequest> = {};

    // Arrays (múltipla seleção) - se houver no NdfCalculatorFilterRequest
    if (queryParams['companyIds']) {
      const companyIds = Array.isArray(queryParams['companyIds'])
        ? queryParams['companyIds']
        : [queryParams['companyIds']];
      filters.companyId = companyIds[0]; // Take first company ID as string
    }

    // Campos únicos
    if (queryParams['internationalCurrencyId']) {
      filters.internationalCurrencyId = queryParams['internationalCurrencyId'];
    }

    if (queryParams['companyId']) {
      filters.companyId = queryParams['companyId'];
    }

    if (queryParams['operationType']) {
      filters.operationType = queryParams['operationType'];
    }

    if (queryParams['reference']) {
      filters.reference = queryParams['reference'];
    }

    // Datas
    if (queryParams['dateFrom']) {
      filters.dateFrom = queryParams['dateFrom'];
    }
    if (queryParams['dateTo']) {
      filters.dateTo = queryParams['dateTo'];
    }

    // Ranges numéricos
    if (queryParams['notionalAmountFrom']) {
      filters.notionalAmountFrom = queryParams['notionalAmountFrom'];
    }
    if (queryParams['notionalAmountTo']) {
      filters.notionalAmountTo = queryParams['notionalAmountTo'];
    }

    // Booleans
    if (queryParams['withHiredOperations']) {
      filters.withHiredOperations = queryParams['withHiredOperations'] === 'true';
    }
    if (queryParams['withHiredOperationsOpen']) {
      filters.withHiredOperationsOpen = queryParams['withHiredOperationsOpen'] === 'true';
    }

    // Se há filtros válidos, aplicar
    const hasValidFilters = Object.keys(filters).some(key => {
      const value = filters[key as keyof NdfCalculatorFilterRequest];
      return (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      );
    });

    if (hasValidFilters) {
      this.currentNdfFilters = filters as NdfCalculatorFilterRequest;
    }
  }

  // Método para atualizar URL com filtros
  updateUrlWithFilters(): void {
    console.log('🔗 Updating URL with filters:', this.currentNdfFilters);

    const queryParams: { [key: string]: string | string[] } = {};

    // Preservar outros query params existentes (não relacionados a filtros)
    const currentParams = this.route.snapshot.queryParams;
    const filterKeys = [
      'internationalCurrencyId',
      'companyId',
      'operationType',
      'reference',
      'dateFrom',
      'dateTo',
      'notionalAmountFrom',
      'notionalAmountTo',
      'withHiredOperations',
      'withHiredOperationsOpen',
      'filter',
    ];

    Object.keys(currentParams).forEach(key => {
      if (!filterKeys.includes(key)) {
        queryParams[key] = currentParams[key];
      }
    });

    if (this.currentNdfFilters) {
      // Campos únicos
      if (this.currentNdfFilters.internationalCurrencyId) {
        queryParams['internationalCurrencyId'] = this.currentNdfFilters.internationalCurrencyId;
      }

      if (this.currentNdfFilters.companyId) {
        queryParams['companyId'] = this.currentNdfFilters.companyId;
      }

      if (this.currentNdfFilters.operationType) {
        queryParams['operationType'] = this.currentNdfFilters.operationType;
      }

      if (this.currentNdfFilters.reference) {
        queryParams['reference'] = this.currentNdfFilters.reference;
      }

      // Datas
      if (this.currentNdfFilters.dateFrom) {
        queryParams['dateFrom'] = this.currentNdfFilters.dateFrom;
      }
      if (this.currentNdfFilters.dateTo) {
        queryParams['dateTo'] = this.currentNdfFilters.dateTo;
      }

      // Ranges numéricos (stored as strings in the model)
      if (this.currentNdfFilters.notionalAmountFrom) {
        queryParams['notionalAmountFrom'] = this.currentNdfFilters.notionalAmountFrom;
      }
      if (this.currentNdfFilters.notionalAmountTo) {
        queryParams['notionalAmountTo'] = this.currentNdfFilters.notionalAmountTo;
      }

      // Booleans
      if (this.currentNdfFilters.withHiredOperations !== undefined) {
        queryParams['withHiredOperations'] = this.currentNdfFilters.withHiredOperations.toString();
      }
      if (this.currentNdfFilters.withHiredOperationsOpen !== undefined) {
        queryParams['withHiredOperationsOpen'] =
          this.currentNdfFilters.withHiredOperationsOpen.toString();
      }
    }

    // Manter o filtro de busca existente se houver
    if (this.search || this.ndfDemandFilter.filter) {
      queryParams['filter'] = this.search || this.ndfDemandFilter.filter || '';
    }

    // Atualizar a URL sem recarregar a página
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'replace' as QueryParamsHandling,
    });
  }
}
